import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Bookmark, Loader2, Users } from 'lucide-react';

/**
 * Mini-popover para agregar un marcador en la página actual.
 * Se ancla al elemento que se le pase como `children` (trigger).
 *
 * Props:
 *  - pagina: número de la página actual
 *  - userTienetaller: bool — si NO tiene taller, ocultamos el switch de compartir
 *  - onConfirm: async ({ nota, compartido }) => marcador|null
 *  - side: 'bottom' | 'top' | 'left' | 'right' (default: 'bottom')
 *  - align: 'start' | 'center' | 'end' (default: 'end')
 */
const PDFBookmarkPopover = ({
  pagina,
  userTienetaller,
  onConfirm,
  side = 'bottom',
  align = 'end',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [nota, setNota] = useState('');
  const [compartido, setCompartido] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setNota('');
    setCompartido(false);
    setSaving(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const result = await onConfirm({ nota: nota.trim(), compartido });
    setSaving(false);
    if (result) {
      reset();
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!o) reset(); setOpen(o); }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        className="w-80 bg-slate-900 border-slate-700 text-white shadow-2xl z-[10000] p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-amber-500/20 rounded-md">
            <Bookmark className="h-4 w-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white leading-none">
              Marcador en pág. <span className="text-amber-400">{pagina}</span>
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400 block mb-1.5">Nota (opcional)</Label>
            <Input
              autoFocus
              placeholder="Ej: Tabla de torques..."
              value={nota}
              onChange={(e) => setNota(e.target.value.slice(0, 200))}
              maxLength={200}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                }
              }}
              className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-9"
              data-testid="bookmark-popover-nota-input"
            />
            <p className="text-[10px] text-slate-500 mt-1 text-right">{nota.length}/200</p>
          </div>

          {userTienetaller && (
            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Users className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" />
                <Label htmlFor="popover-compartir" className="text-xs text-slate-200 cursor-pointer">
                  Compartir con mi taller
                </Label>
              </div>
              <Switch
                id="popover-compartir"
                checked={compartido}
                onCheckedChange={setCompartido}
                disabled={saving}
                data-testid="bookmark-popover-compartir"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="text-slate-300 hover:text-white hover:bg-slate-800 h-8"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white h-8"
              data-testid="bookmark-popover-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Guardando</>
              ) : (
                <><Bookmark className="h-3.5 w-3.5 mr-1.5" />Guardar</>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PDFBookmarkPopover;
